import type { SpeakerProfile, TokenSegment } from "./types.ts";

export type LabelFormatter = (profile: SpeakerProfile) => string;

const defaultLabel: LabelFormatter = (p) => `[${p.name} — ${p.role}]`;

/**
 * Convierte segundos decimales al formato de timestamp SRT: HH:MM:SS,mmm.
 */
function secondsToSrtTimestamp(seconds: number): string {
  const totalMs = Math.round(seconds * 1000);
  const ms = totalMs % 1000;
  const totalSec = Math.floor(totalMs / 1000);
  const sec = totalSec % 60;
  const totalMin = Math.floor(totalSec / 60);
  const min = totalMin % 60;
  const hrs = Math.floor(totalMin / 60);

  const pad2 = (n: number) => String(n).padStart(2, "0");
  const pad3 = (n: number) => String(n).padStart(3, "0");

  return `${pad2(hrs)}:${pad2(min)}:${pad2(sec)},${pad3(ms)}`;
}

/**
 * Genera el SRT etiquetado a partir de TokenSegments.
 *
 * Formato de cada bloque:
 * ```
 * <index>
 * HH:MM:SS,mmm --> HH:MM:SS,mmm
 * [Nombre — Cargo]: texto del segmento
 * ```
 *
 * Si no hay perfil para un speakerIndex lanza error explícito para que
 * el caller sepa que el estado es inconsistente.
 */
export function generateTaggedSrt(
  segments: TokenSegment[],
  profiles: Map<number, SpeakerProfile>,
  labelFormat: LabelFormatter = defaultLabel,
): string {
  const blocks = segments.map((seg) => {
    const profile = profiles.get(seg.speakerIndex);
    if (!profile) {
      throw new Error(
        `No hay perfil para Speaker ${seg.speakerIndex} (segmento ${seg.index}). ` +
          `Perfiles disponibles: ${[...profiles.keys()].sort().join(", ")}`,
      );
    }

    const label = labelFormat(profile);
    const start = secondsToSrtTimestamp(seg.startTime);
    const end = secondsToSrtTimestamp(seg.endTime);

    // 1-based según spec SRT.
    return `${seg.index + 1}\n${start} --> ${end}\n${label}: ${seg.text}`;
  });

  return blocks.join("\n\n");
}
